// Sugalarm — kiosk-local volume agent client (cycle 22).
//
// These calls hit http://127.0.0.1:8765 directly, NOT the Fly API
// origin via apiClient. The agent runs on the Pi alongside Chromium;
// see kiosk_agent/README.md and KIOSK_SETUP.md section 9b.

import type { ApiError, ApiErrorKind, ApiResult } from "../lib/types";

const AGENT_BASE = "http://127.0.0.1:8765";
const TIMEOUT_MS = 1500;

export interface VolumeState {
  level: number;
  muted: boolean;
  control: string;
}

export interface SetVolumeBody {
  level?: number;
  muted?: boolean;
}

function asError(kind: ApiErrorKind, status: number, message: string): ApiError {
  return { kind, status, message };
}

async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${AGENT_BASE}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers ?? {}),
      },
    });
    if (!res.ok) {
      const kind: ApiErrorKind =
        res.status === 422
          ? "validation"
          : res.status >= 500
          ? "server"
          : "unknown";
      return {
        ok: false,
        error: asError(kind, res.status, `agent returned ${res.status}`),
      };
    }
    const data = (await res.json()) as T;
    return { ok: true, data };
  } catch {
    // AbortError (timeout) and network errors both land here. The hook
    // treats both as "agent unreachable" → button hidden.
    return {
      ok: false,
      error: asError("network", 0, "kiosk agent unreachable"),
    };
  } finally {
    window.clearTimeout(t);
  }
}

export function getVolume(): Promise<ApiResult<VolumeState>> {
  return call<VolumeState>("/volume");
}

export function setVolume(body: SetVolumeBody): Promise<ApiResult<VolumeState>> {
  return call<VolumeState>("/volume", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}
